import { localized } from "@/lib/i18n";
import { useState } from "react";
import { toast } from "sonner";
import { Rocket, X, CheckCircle2, AlertCircle } from "lucide-react";
import { LocationAutocomplete, SkillAutocomplete, AudienceMeter } from "@/components/campaign/Autocomplete";
import { DiscountField, TermsCheck, discountOf } from "@/components/campaign/CheckoutExtras";
import { BillingFields, useBilling } from "@/components/campaign/BillingFields";
import { ServiceCatalog, money } from "@/components/campaign/ServiceCatalog";
import { useCart } from "@/context/CartContext";
import { SuggestedServices, useSuggestions } from "@/components/campaign/SuggestedServices";
import { CartBox, cartLines } from "@/components/campaign/CartBox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LangContext";
import { TID } from "@/constants/testIds";

const SUGGESTED_INTERESTS = { it: ["Eventi", "Wedding", "Ristorazione", "Real Estate", "Fitness", "Finance", "Fashion", "Tech"],
  en: ["Events", "Wedding", "Restaurants", "Real Estate", "Fitness", "Finance", "Fashion", "Tech"] };
const scrollToStep = (n) => document.querySelector(`[data-testid="step-${n}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });

const MissingBox = ({ missing, tried }) => {
  const { tr } = useLang();
  if (!missing.length) return null;
  return (
    <div data-testid="form-missing" className={`text-sm border p-4 ${tried ? "border-[#E0262C] bg-[#fdecec] text-[#2B3036]" : "border-dashed border-[#cfd6d9] text-[#5b6168]"}`}>
      <div className="flex items-center gap-2 font-medium mb-2"><AlertCircle className="w-4 h-4 text-[#E0262C]" />{tr("Per procedere manca ancora:", "Still missing to continue:")}</div>
      <ul className="space-y-1">
        {missing.map((m) => (
          <li key={m.n}><button type="button" data-testid={`missing-step-${m.n}`} onClick={() => scrollToStep(m.n)} className="text-left underline decoration-dotted hover:text-[#E0262C]">
            <strong>{tr("Passo", "Step")} {m.n}</strong> — {m.text}</button></li>
        ))}
      </ul>
    </div>
  );
};

const Step = ({ n, title, hint, done, error, children, last }) => (
  <div data-testid={`step-${n}`} className="grid grid-cols-[64px_1fr] sm:grid-cols-[96px_1fr] gap-4 sm:gap-6">
    <div className="flex flex-col items-center">
      <div className={`font-display text-5xl sm:text-7xl leading-none transition-colors ${done || error ? "text-[#E0262C]" : "text-[#2B3036]"}`}>{n}</div>
      {error && <AlertCircle data-testid={`step-${n}-error`} className="w-5 h-5 text-[#E0262C] mt-2" />}
      {done && <CheckCircle2 data-testid={`step-${n}-done`} className="w-5 h-5 text-[#E0262C] mt-2" />}
      {!last && <div className="flex-1 w-px bg-[#cfd6d9] mt-3" />}
    </div>
    <div className="pb-12 min-w-0">
      <h3 className="font-display text-2xl leading-tight">{title}</h3>
      {hint && <p className="text-sm text-[#5b6168] mt-1 mb-4 max-w-2xl">{hint}</p>}
      {children}
    </div>
  </div>
);

const GenderAge = ({ gender, setGender, ageRange, setAgeRange }) => {
  const { tr } = useLang();
  const opts = [{ v: "all", tid: TID.genderAll, label: tr("Tutti", "All") }, { v: "male", tid: TID.genderMale, label: tr("Uomo", "Male") }, { v: "female", tid: TID.genderFemale, label: tr("Donna", "Female") }];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
      <RadioGroup value={gender} onValueChange={setGender} className="grid grid-cols-3 gap-2">
        {opts.map((o) => (
          <label key={o.v} htmlFor={`g-${o.v}`}
            className={`cursor-pointer border p-3 flex items-center justify-center transition-colors ${gender === o.v ? "border-[#2B3036] bg-[#2B3036] text-white" : "border-[#cfd6d9] bg-white hover:border-[#a1a1aa]"}`}>
            <RadioGroupItem data-testid={o.tid} value={o.v} id={`g-${o.v}`} className="sr-only" />
            <span className="text-sm font-medium">{o.label}</span>
          </label>
        ))}
      </RadioGroup>
      <div className="bg-white border border-[#cfd6d9] px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-widest text-[#71717a] font-mono">{tr("Fascia di età", "Age range")}</span>
          <span className="font-mono text-sm">{ageRange[0]} — {ageRange[1]}</span>
        </div>
        <Slider data-testid={TID.ageSlider} min={18} max={80} step={1} value={ageRange} onValueChange={setAgeRange} />
      </div>
    </div>
  );
};

const Interests = ({ interests, setInterests }) => {
  const { lang, tr } = useLang();
  const [input, setInput] = useState("");
  const add = (val) => { const v = val.trim(); if (v && !interests.includes(v)) setInterests([...interests, v]); setInput(""); };
  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap gap-2 mb-3" data-testid={TID.interestsList}>
        {interests.map((int, i) => (
          <span key={int} className="inline-flex items-center gap-1.5 bg-white border border-[#cfd6d9] px-3 py-1.5 text-sm">
            {int}
            <button onClick={() => setInterests(interests.filter((_, idx) => idx !== i))} className="text-[#a1a1aa] hover:text-[#E0262C]" data-testid={`remove-interest-${i}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <Input data-testid={TID.interestsInput} value={input} onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(input); } }}
        placeholder={tr("Aggiungi interesse e premi Invio", "Add an interest and press Enter")} className="rounded-sm border-[#cfd6d9] bg-white h-11" />
      <div className="flex flex-wrap gap-1.5 mt-2">
        {localized(SUGGESTED_INTERESTS, lang).filter((i) => !interests.includes(i)).map((i) => (
          <button key={i} onClick={() => add(i)} data-testid={`suggest-interest-${i}`}
            className="text-xs text-[#5b6168] hover:text-[#E0262C] font-mono border border-dashed border-[#cfd6d9] px-2 py-1">+ {i}</button>
        ))}
      </div>
    </div>
  );
};

export const TargetingForm = ({ onLaunch, loading, analysisId }) => {
  const { tr } = useLang();
  const [location, setLocation] = useState(null);
  const [skills, setSkills] = useState([]);
  const [notes, setNotes] = useState("");
  const [gender, setGender] = useState("all");
  const [ageRange, setAgeRange] = useState([25, 55]);
  const [interests, setInterests] = useState([]);
  const { cart, setQty, catalog } = useCart();
  const [discount, setDiscount] = useState(null);
  const lines = cartLines(catalog, cart);
  const gross = lines.reduce((a, l) => a + l.subtotal, 0);
  const [terms, setTerms] = useState(false);
  const [billing, setBilling] = useBilling();
  const cartOk = lines.length > 0;
  const [tried, setTried] = useState(false);
  const missing = [
    !location && { n: 1, text: tr("scegli la location (città, regione o nazione) e selezionala dalla tendina", "choose the location (city, region or country) and select it from the list") },
    !skills.length && { n: 2, text: tr("aggiungi almeno un settore/skill selezionandolo dalla tendina", "add at least one sector/skill by selecting it from the list") },
    !cartOk && { n: 6, text: tr("aggiungi almeno un servizio o kit al carrello", "add at least one service or kit to the cart") },
    !terms && { n: 8, text: tr("accetta i Termini e Condizioni e la Privacy Policy", "accept the Terms and Conditions and the Privacy Policy") },
  ].filter(Boolean);
  const err = (n) => tried && missing.some((m) => m.n === n);
  const total = gross - discountOf(discount, gross);
  const audience = skills.some((s) => s.audience != null) ? skills.reduce((a, s) => a + (s.audience || 0), 0) : null;
  const hasBilling = Object.values(billing).some((v) => v.trim());
  const suggestions = useSuggestions(audience, analysisId);

  const launch = () => {
    if (!missing.length) return submit();
    setTried(true);
    toast.error(tr("Manca qualcosa: ", "Something is missing: ") + missing.map((m) => `${tr("passo", "step")} ${m.n}`).join(", "));
    scrollToStep(missing[0].n);
  };
  const submit = () => onLaunch({
    area: location.name, skill: skills.map((s) => s.label).join(", ").slice(0, 300), notes: notes.trim(), gender,
    age_min: ageRange[0], age_max: ageRange[1], interests, currency: "chf", audience_estimate: audience,
    items: lines.map((l) => ({ id: l.id, qty: l.qty })), discount_code: discount?.code || null, terms_accepted: terms,
    billing: hasBilling ? billing : null,
  });

  return (
    <section data-testid={TID.targetingForm} className="border border-[#cfd6d9] p-6 sm:p-10 bg-[#f4f6f7]">
      <div className="mb-12">
        <h2 className="font-display text-3xl sm:text-4xl mb-2">{tr("Configura la tua campagna", "Set up your campaign")}</h2>
        <p className="text-sm text-[#5b6168] max-w-2xl">{tr("Ti guidiamo passo passo: in meno di un minuto la tua campagna è pronta.", "We guide you step by step: your campaign is ready in less than a minute.")}</p>
      </div>

      <Step n={1} done={!!location} error={err(1)} title={tr("Dove vuoi trovare i tuoi clienti?", "Where do you want to find your customers?")} hint={tr("Scegli prima la location: città, regione/cantone o nazione.", "Choose the location first: city, region/canton or country.")}>
        <div className="max-w-3xl"><LocationAutocomplete value={location} onChange={setLocation} /></div>
      </Step>

      <Step n={2} done={skills.length > 0} error={err(2)} title={tr("Quale settore o skill cerchi?", "Which sector or skill are you looking for?")}
        hint={tr("Scrivi e scegli dalla tendina: per ogni skill vedi quante persone sono potenzialmente interessate nella location scelta. Puoi aggiungerne più di una.",
          "Type and pick from the list: for each skill you see how many people are potentially interested in the chosen location. You can add more than one.")}>
        <div className="max-w-3xl">
          <SkillAutocomplete location={location} skills={skills} onChange={setSkills} />
          <AudienceMeter location={location} skills={skills} />
        </div>
      </Step>

      <Step n={3} done={notes.trim().length > 0} title={tr("Vuoi aggiungere dettagli?", "Want to add details?")} hint={tr("Facoltativo: descrivi meglio il cliente ideale.", "Optional: describe your ideal customer.")}>
        <textarea data-testid="notes-input" value={notes} maxLength={500} rows={3} onChange={(e) => setNotes(e.target.value)}
          placeholder={tr("es. Aziende che organizzano eventi aziendali, location, catering...", "e.g. Companies organising corporate events, venues, catering...")}
          className="w-full max-w-3xl rounded-sm border border-[#cfd6d9] bg-white p-3 text-sm focus:outline-none focus:border-[#2B3036]" />
      </Step>

      <Step n={4} done title={tr("A chi ti rivolgi?", "Who is your audience?")} hint={tr("Genere e fascia d'età del pubblico.", "Gender and age range of the audience.")}>
        <GenderAge gender={gender} setGender={setGender} ageRange={ageRange} setAgeRange={setAgeRange} />
      </Step>

      <Step n={5} done={interests.length > 0} title={tr("Interessi aggiuntivi", "Additional interests")} hint={tr("Facoltativo: aiutano a profilare meglio i contatti.", "Optional: they help profile contacts better.")}>
        <Interests interests={interests} setInterests={setInterests} />
      </Step>

      <Step n={6} done={cartOk} error={err(6)} title={tr("Scegli i servizi per la tua campagna", "Choose the services for your campaign")} hint={tr("Clicca sulle card per aggiungere kit e servizi al carrello. Ogni servizio dura 30 giorni ed è cumulabile.", "Click the cards to add kits and services to the cart. Each service lasts 30 days and can be stacked.")}>
        <SuggestedServices suggestions={suggestions} items={catalog} cart={cart} setQty={setQty} />
        <ServiceCatalog items={catalog} cart={cart} setQty={setQty} suggested={suggestions.map((x) => x.id)} />
      </Step>

      <Step n={7} done={hasBilling} title={tr("Dati di fatturazione", "Billing details")} hint={tr("Facoltativi: ragione sociale, indirizzo e P.IVA/UID da riportare in fattura. Li ricordiamo per la prossima volta.", "Optional: company name, address and VAT no./UID to show on the invoice. We remember them for next time.")}>
        <BillingFields value={billing} onChange={setBilling} />
      </Step>

      <Step n={8} last done={false} title={tr("Paga e attiva la campagna", "Pay and activate the campaign")} hint={tr("Dopo il pagamento il nostro team avvia la campagna e ti avvisa via email e nell'area riservata.", "After payment our team starts the campaign and notifies you by email and in your reserved area.")}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl items-start">
          <CartBox lines={lines} setQty={setQty} discount={discount} />
          <div className="space-y-4">
            <DiscountField gross={gross} discount={discount} setDiscount={setDiscount} />
            <MissingBox missing={missing} tried={tried} />
            <TermsCheck checked={terms} onChange={setTerms} />
            <Button data-testid={TID.launchBtn} onClick={launch} disabled={loading}
              className="w-full aurora-gradient hover:opacity-90 text-white rounded-sm h-14 px-8 font-medium text-base">
              <Rocket className="w-5 h-5 mr-2" strokeWidth={2.5} />
              {tr("Paga", "Pay")} {money(total)} {tr("e attiva la campagna", "and activate the campaign")}
            </Button>
          </div>
        </div>
      </Step>
    </section>
  );
};
